import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  name: string;
  href: string;
  icon: string;
  role?: 'student' | 'tutor' | 'admin';
  badge?: string;
}

const navigationItems: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: '🏠',
  },
  {
    name: 'Profile',
    href: '/profile',
    icon: '👤',
  },
  {
    name: 'Courses',
    href: '/courses',
    icon: '📚',
  },
  {
    name: 'My Enrollments',
    href: '/enrollments',
    icon: '📖',
    role: 'student',
  },
  {
    name: 'Exams',
    href: '/exams',
    icon: '📝',
  },
  {
    name: 'Certificates',
    href: '/certificates',
    icon: '🎓',
    role: 'student',
  },
  {
    name: 'Leaderboard',
    href: '/leaderboard',
    icon: '🏆',
  },
  {
    name: 'Badges',
    href: '/badges',
    icon: '🏅',
  },
  {
    name: 'Progress',
    href: '/progress',
    icon: '📊',
    role: 'student',
  },
  {
    name: 'Create Course',
    href: '/courses/create',
    icon: '➕',
    role: 'tutor',
  },
  {
    name: 'Media Library',
    href: '/media',
    icon: '🖼️',
    role: 'tutor',
  },
  {
    name: 'Spaces',
    href: '/spaces',
    icon: '🏛️',
    role: 'tutor',
  },
  {
    name: 'Create Exam',
    href: '/exams/create',
    icon: '✏️',
    role: 'tutor',
  },
  {
    name: 'User Management',
    href: '/admin/users',
    icon: '👥',
    role: 'admin',
  },
  {
    name: 'Media Library',
    href: '/media',
    icon: '🖼️',
    role: 'admin',
  },
  {
    name: 'Audit Log',
    href: '/audit',
    icon: '🔍',
    role: 'admin',
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: '📈',
    role: 'admin',
  },
  {
    name: 'System Settings',
    href: '/admin/settings',
    icon: '⚙️',
    role: 'admin',
  },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const filteredNavigation = navigationItems.filter(item =>
    !item.role || item.role === user?.role
  );

  const handleLogout = () => {
    logout();
  };

  return (
    <div className={`bg-white shadow-lg h-full flex flex-col shrink-0 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-52'
      } ${!user ? 'hidden' : ''}`}>
      {/* Header */}
      <div className="p-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <h1 className="text-base font-bold text-gray-900">Phino LMS</h1>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-md hover:bg-gray-100"
          >
            {isCollapsed ? '→' : '←'}
          </button>
        </div>
      </div>

      {/* User Info */}
      {!isCollapsed && user && (
        <div className="p-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-medium">
                {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-[10px] text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-3">
        <ul className="space-y-1">
          {filteredNavigation?.map((item) => {
            const isActive = location.pathname === item.href;

            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex items-center space-x-2 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <span className="text-sm">{item.icon}</span>
                  {!isCollapsed && (
                    <>
                      <span>{item.name}</span>
                      {item.badge && (
                        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 shrink-0">
        {!isCollapsed ? (
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-2 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        ) : (
          <button
            onClick={handleLogout}
            className="w-full p-2 text-gray-700 hover:bg-gray-100 rounded-md"
            title="Logout"
          >
            🚪
          </button>
        )}
      </div>
    </div>
  );
} 